import React, { useState, useEffect } from 'react';
import { Modal, Button, List, Input, Form, Select, Space, Popconfirm, message, DatePicker, Card, Tag } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import './ModalFilter.css';

const { Option } = Select;
const { RangePicker } = DatePicker;

// Definição padrão das colunas (frete)
const DEFAULT_AVAILABLE_COLUMNS = [
    { key: 'firstDelivery.dateColeta', label: 'Data Coleta', type: 'date' },
    { key: 'numberSerial', label: 'Código Frete', type: 'text' },
    { key: 'clientPayment.name', label: 'Nome Pagador', type: 'text' },
    { key: 'freight.getDriverFreight.name', label: 'Nome Motorista', type: 'text' },
    { key: 'firstDelivery.city', label: 'Origem', type: 'text' },
    { key: 'lastDelivery.city', label: 'Destino', type: 'text' },
    { key: 'freight.valueFreightage', label: 'Valor do Frete', type: 'number' },
    { key: 'driver.valueNegotiated', label: 'Pagando ao Motorista', type: 'number' },
    { key: 'status.describe', label: 'Status', type: 'select' }
];

// Operadores disponíveis por tipo de coluna
const OPERATORS = {
    text: [
        { value: 'contains', label: 'Contém' },
        { value: 'equals', label: 'Igual a' },
        { value: 'startsWith', label: 'Começa com' },
        { value: 'endsWith', label: 'Termina com' }
    ],
    number: [
        { value: 'equals', label: 'Igual a' },
        { value: 'notEquals', label: 'Diferente de' },
        { value: 'greaterThan', label: 'Maior que' },
        { value: 'lessThan', label: 'Menor que' },
        { value: 'greaterThanOrEqual', label: 'Maior ou igual a' },
        { value: 'lessThanOrEqual', label: 'Menor ou igual a' }
    ],
    date: [
        { value: 'equals', label: 'Igual a' },
        { value: 'before', label: 'Antes de' },
        { value: 'after', label: 'Depois de' },
        { value: 'between', label: 'Entre' }
    ],
    select: [
        { value: 'equals', label: 'Igual a' },
        { value: 'in', label: 'Em' }
    ],
    'multi-select': [
        { value: 'equals', label: 'Igual a' },
        { value: 'in', label: 'Em' }
    ]
};

const DEFAULT_STATUS_OPTIONS = [
    'Pendente de contratação',
    'Em transito',
    'Contratado',
    'Finalizado',
    'Cancelado'
];

const isValidDayjs = (d) => dayjs.isDayjs(d) && d.isValid();

/** Serializa qualquer valor de data para YYYY-MM-DD (calendário local, sem shift UTC). */
function serializeDateForStorage(value) {
    if (value == null || value === '') return null;

    if (isValidDayjs(value)) {
        return value.format('YYYY-MM-DD');
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return dayjs(value).format('YYYY-MM-DD');
    }

    const raw = String(value).trim();
    if (!raw) return null;

    const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnlyMatch) {
        return `${dateOnlyMatch[1]}-${dateOnlyMatch[2]}-${dateOnlyMatch[3]}`;
    }

    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

function parseStoredDateToDayjs(value) {
    const serialized = serializeDateForStorage(value);
    if (!serialized) return null;

    const [year, month, day] = serialized.split('-').map(Number);
    const parsed = dayjs(new Date(year, month - 1, day, 0, 0, 0, 0));
    return parsed.isValid() ? parsed : null;
}

function serializeConditionValueForStorage(condition, columnConfig) {
    if (columnConfig?.type !== 'date' || condition.value == null) {
        return condition;
    }

    if (condition.operator === 'between' && Array.isArray(condition.value)) {
        const serialized = condition.value.map(serializeDateForStorage);
        if (!serialized[0] || !serialized[1]) {
            return { ...condition, value: null };
        }
        return { ...condition, value: serialized };
    }

    const serialized = serializeDateForStorage(condition.value);
    return { ...condition, value: serialized };
}

function hydrateConditionValueFromStorage(condition, columnConfig) {
    if (columnConfig?.type !== 'date' || !condition.value) {
        return condition;
    }

    if (condition.operator === 'between' && Array.isArray(condition.value)) {
        const dates = condition.value.map(parseStoredDateToDayjs);
        if (dates[0] && dates[1]) {
            return { ...condition, value: dates };
        }
        return { ...condition, value: null };
    }

    const parsed = parseStoredDateToDayjs(condition.value);
    return { ...condition, value: parsed };
}

function isConditionValueFilled(condition, columnConfig) {
    if (!condition.column || !condition.operator) return false;

    if (columnConfig?.type === 'date' && condition.operator === 'between') {
        return (
            Array.isArray(condition.value) &&
            Boolean(serializeDateForStorage(condition.value[0])) &&
            Boolean(serializeDateForStorage(condition.value[1]))
        );
    }

    if (Array.isArray(condition.value)) {
        return condition.value.length > 0;
    }

    return condition.value != null && condition.value !== '';
}

export default function ModalFilter({
    visible,
    onClose,
    onApply,
    availableColumns = DEFAULT_AVAILABLE_COLUMNS,
    selectOptionsMap = { 'status.describe': DEFAULT_STATUS_OPTIONS },
    storageKeySuffix = 'customFilters',
}) {
    const user = useSelector(state => state.user);
    const [filters, setFilters] = useState([]);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingFilter, setEditingFilter] = useState(null);
    const [form] = Form.useForm();
    const [filterConditions, setFilterConditions] = useState([]);

    const storageKey = (uid) => `${uid}:${storageKeySuffix}`;

    const getOptionsForColumn = (columnKey) => {
        const options = selectOptionsMap?.[columnKey] || [];
        return options.map((option) => {
            if (typeof option === 'string') {
                return { label: option, value: option };
            }
            return {
                label: option?.label ?? option?.value ?? '',
                value: option?.value ?? option?.label ?? '',
            };
        });
    };

    // Carregar filtragens do localStorage
    useEffect(() => {
        if (!user?.uid) return;

        const savedFilters = localStorage.getItem(storageKey(user.uid));
        if (savedFilters) {
            try {
                setFilters(JSON.parse(savedFilters));
            } catch (error) {
                console.error('Erro ao carregar filtros:', error);
                setFilters([]);
            }
        }
    }, [user?.uid, visible]);

    // Salvar filtragens no localStorage
    const saveFiltersToStorage = (newFilters) => {
        if (!user?.uid) return;
        localStorage.setItem(storageKey(user.uid), JSON.stringify(newFilters));
        setFilters(newFilters);
    };

    // Adicionar nova condição de filtro
    const addFilterCondition = () => {
        setFilterConditions([
            ...filterConditions,
            {
                id: Date.now().toString(),
                column: null,
                operator: null,
                value: null
            }
        ]);
    };

    // Remover condição de filtro
    const removeFilterCondition = (id) => {
        setFilterConditions(filterConditions.filter(cond => cond.id !== id));
    };

    // Atualizar condição de filtro
    const updateFilterCondition = (id, field, value) => {
        setFilterConditions(filterConditions.map(cond => {
            if (cond.id === id) {
                const updated = { ...cond, [field]: value };
                
                // Se a coluna mudou, resetar operador e valor
                if (field === 'column') {
                    updated.operator = null;
                    updated.value = null;
                }

                return updated;
            }
            return cond;
        }));
    };

    // Criar nova filtragem
    const handleCreateFilter = (values) => {
        if (filterConditions.length === 0) {
            message.warning('Adicione pelo menos uma condição de filtro!');
            return;
        }

        const invalidConditions = filterConditions.some((cond) => {
            const columnConfig = availableColumns.find((col) => col.key === cond.column);
            return !isConditionValueFilled(cond, columnConfig);
        });

        if (invalidConditions) {
            message.warning('Preencha todas as condições de filtro!');
            return;
        }

        const conditionsForStorage = filterConditions.map((condition) => {
            const columnConfig = availableColumns.find((col) => col.key === condition.column);
            return serializeConditionValueForStorage(condition, columnConfig);
        });

        const newFilter = {
            id: Date.now().toString(),
            name: values.name,
            conditions: conditionsForStorage,
            createdAt: new Date().toISOString(),
        };

        const updatedFilters = [...filters, newFilter];
        saveFiltersToStorage(updatedFilters);

        form.resetFields();
        setFilterConditions([]);
        setShowCreateForm(false);
        message.success('Filtro criado com sucesso!');
    };

    // Editar filtragem existente
    const handleEditFilter = (values) => {
        if (filterConditions.length === 0) {
            message.warning('Adicione pelo menos uma condição de filtro!');
            return;
        }

        const invalidConditions = filterConditions.some((cond) => {
            const columnConfig = availableColumns.find((col) => col.key === cond.column);
            return !isConditionValueFilled(cond, columnConfig);
        });

        if (invalidConditions) {
            message.warning('Preencha todas as condições de filtro!');
            return;
        }

        const conditionsForStorage = filterConditions.map((condition) => {
            const columnConfig = availableColumns.find((col) => col.key === condition.column);
            return serializeConditionValueForStorage(condition, columnConfig);
        });

        const updatedFilters = filters.map(filter =>
            filter.id === editingFilter.id 
                ? { 
                    ...filter, 
                    name: values.name,
                    conditions: conditionsForStorage,
                    updatedAt: new Date().toISOString() 
                }
                : filter
        );

        saveFiltersToStorage(updatedFilters);

        form.resetFields();
        setFilterConditions([]);
        setEditingFilter(null);
        setShowCreateForm(false);
        message.success('Filtro atualizado com sucesso!');
    };

    // Deletar filtragem
    const handleDeleteFilter = (filterId) => {
        const updatedFilters = filters.filter(filter => filter.id !== filterId);
        saveFiltersToStorage(updatedFilters);
        message.success('Filtro removido com sucesso!');
    };

    // Aplicar filtragem
    const handleApplyFilter = (filter) => {
        if (onApply) {
            onApply(filter);
        }
        onClose();
        message.success(`Filtro "${filter.name}" aplicado!`);
    };

    // Abrir formulário de edição
    const handleEditClick = (filter) => {
        setEditingFilter(filter);
        form.setFieldsValue({
            name: filter.name
        });
        
        const conditionsWithDates = (filter.conditions || []).map((condition) => {
            const columnConfig = availableColumns.find((col) => col.key === condition.column);
            return hydrateConditionValueFromStorage(condition, columnConfig);
        });

        setFilterConditions(conditionsWithDates);
        setShowCreateForm(true);
    };

    // Cancelar criação/edição
    const handleCancelForm = () => {
        form.resetFields();
        setFilterConditions([]);
        setShowCreateForm(false);
        setEditingFilter(null);
    };

    // Validador da data para normalizar o valor
    const normalizeDateValue = (value, isRange = false) => {
        if (isRange) {
            if (Array.isArray(value) && value.length === 2) {
                const dates = value.map(date => {
                    if (isValidDayjs(date)) return date;
                    if (typeof date === 'string' && date) return dayjs(date);
                    return null;
                });
                
                if (dates[0] && dates[1] && dates[0].isValid() && dates[1].isValid()) {
                    return dates;
                }
            }
            return null;
        }
    
        if (isValidDayjs(value)) {
            return value;
        }
        
        if (typeof value === 'string' && value) {
            const date = dayjs(value);
            return date.isValid() ? date : null;
        }
    
        return null;
    };

    // Renderizar campo de valor baseado no tipo de coluna e operador
    const renderValueInput = (condition) => {
        const columnConfig = availableColumns.find(col => col.key === condition.column);

        if (!columnConfig || !condition.operator) {
            return null;
        }

        switch (columnConfig.type) {
            case 'date':
                if (condition.operator === 'between') {
                    return (
                        <RangePicker
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                            value={normalizeDateValue(condition.value, true)}
                            onChange={(dates) => updateFilterCondition(condition.id, 'value', dates)}
                        />
                    );
                }
                return (
                    <DatePicker
                        style={{ width: '100%' }}
                        format="DD/MM/YYYY"
                        value={normalizeDateValue(condition.value)}
                        onChange={(date) => updateFilterCondition(condition.id, 'value', date)}
                    />
                );

            case 'number':
                return (
                    <Input
                        type="number"
                        placeholder="Valor"
                        value={condition.value}
                        onChange={(e) => updateFilterCondition(condition.id, 'value', e.target.value)}
                    />
                );

            case 'select':
            case 'multi-select':
                if (condition.operator === 'in') {
                    return (
                        <Select
                            mode="multiple"
                            placeholder="Selecione os valores"
                            style={{ width: '100%' }}
                            value={condition.value}
                            onChange={(value) => updateFilterCondition(condition.id, 'value', value)}
                        >
                            {getOptionsForColumn(condition.column).map(option => (
                                <Option key={option.value} value={option.value}>{option.label}</Option>
                            ))}
                        </Select>
                    );
                }
                return (
                    <Select
                        placeholder="Selecione o valor"
                        style={{ width: '100%' }}
                        value={condition.value}
                        onChange={(value) => updateFilterCondition(condition.id, 'value', value)}
                    >
                        {getOptionsForColumn(condition.column).map(option => (
                            <Option key={option.value} value={option.value}>{option.label}</Option>
                        ))}
                    </Select>
                );

            case 'text':
            default:
                return (
                    <Input
                        placeholder="Valor"
                        value={condition.value}
                        onChange={(e) => updateFilterCondition(condition.id, 'value', e.target.value)}
                    />
                );
        }
    };

    return (
        <Modal
            title={showCreateForm ? (editingFilter ? 'Editar Filtragem' : 'Criar Nova Filtragem') : 'Filtros Salvos'}
            visible={visible}
            onCancel={onClose}
            width={900}
            footer={null}
            className="advanced-filter-modal"
        >
            {!showCreateForm ? (
                <>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        className="btn-default-primary"
                        onClick={() => {
                            setShowCreateForm(true);
                            addFilterCondition();
                        }}
                        style={{ marginBottom: 16, width: '100%' }}
                        size="large"
                    >
                        Criar Nova Filtragem Avançada
                    </Button>

                    <List
                        dataSource={filters}
                        locale={{ emptyText: 'Nenhuma filtragem criada ainda' }}
                        renderItem={(filter) => (
                            <List.Item
                                className="filter-list-item-custom"
                                actions={[
                                    <Button
                                        type="link"
                                        icon={<EditOutlined />}
                                        onClick={() => handleEditClick(filter)}
                                    >
                                        Editar
                                    </Button>,
                                    <Popconfirm
                                        title="Tem certeza que deseja excluir esta filtragem?"
                                        onConfirm={() => handleDeleteFilter(filter.id)}
                                        okText="Sim"
                                        cancelText="Não"
                                    >
                                        <Button
                                            type="link"
                                            danger
                                            icon={<DeleteOutlined />}
                                        >
                                            Excluir
                                        </Button>
                                    </Popconfirm>,
                                    <Button
                                        type="primary"
                                        onClick={() => handleApplyFilter(filter)}
                                        className="btn-default-primary"
                                    >
                                        Aplicar
                                    </Button>,
                                ]}
                            >
                                <List.Item.Meta
                                    title={<span style={{ fontSize: 16, fontWeight: 600 }}>{filter.name}</span>}
                                    description={
                                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                            <div>
                                                {filter.conditions?.map((cond, index) => {
                                                    const columnConfig = availableColumns.find(col => col.key === cond.column);

                                                    return (
                                                        <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                                                            <strong>{columnConfig?.label}</strong> 
                                                        </Tag>
                                                    );
                                                })}
                                            </div>
                                        </Space>
                                    }
                                />
                            </List.Item>
                        )}
                    />

                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                        <Button onClick={onClose}>
                            Fechar
                        </Button>
                    </div>
                </>
            ) : (
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={editingFilter ? handleEditFilter : handleCreateFilter}
                >
                    <Form.Item
                        name="name"
                        label="Nome da Filtragem"
                        rules={[{ required: true, message: 'Por favor, insira um nome para a filtragem' }]}
                    >
                        <Input placeholder="Ex: Fretes Pendentes São Paulo" size="large" />
                    </Form.Item>

                    <Card 
                        title="Condições de Filtro" 
                        className="filter-conditions-card"
                        extra={
                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={addFilterCondition}
                                size="small"
                            >
                                Adicionar Condição
                            </Button>
                        }
                    >
                        {filterConditions.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
                                Nenhuma condição adicionada. Clique em "Adicionar Condição" para começar.
                            </div>
                        ) : (
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                {filterConditions.map((condition, index) => (
                                    <Card
                                        key={condition.id}
                                        size="small"
                                        className="filter-condition-item"
                                        title={`Condição ${index + 1}`}
                                        extra={
                                            <Button
                                                type="text"
                                                danger
                                                size="small"
                                                icon={<CloseCircleOutlined />}
                                                onClick={() => removeFilterCondition(condition.id)}
                                            />
                                        }
                                    >
                                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                            <div>
                                                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Coluna</label>
                                                <Select
                                                    placeholder="Selecione a coluna"
                                                    style={{ width: '100%' }}
                                                    value={condition.column}
                                                    onChange={(value) => updateFilterCondition(condition.id, 'column', value)}
                                                >
                                                    {availableColumns.map(col => (
                                                        <Option key={col.key} value={col.key}>{col.label}</Option>
                                                    ))}
                                                </Select>
                                            </div>

                                            {condition.column && (
                                                <div>
                                                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Operador</label>
                                                    <Select
                                                        placeholder="Selecione o operador"
                                                        style={{ width: '100%' }}
                                                        value={condition.operator}
                                                        onChange={(value) => updateFilterCondition(condition.id, 'operator', value)}
                                                    >
                                                        {OPERATORS[availableColumns.find(col => col.key === condition.column)?.type]?.map(op => (
                                                            <Option key={op.value} value={op.value}>{op.label}</Option>
                                                        ))}
                                                    </Select>
                                                </div>
                                            )}

                                            {condition.operator && (
                                                <div>
                                                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Valor</label>
                                                    {renderValueInput(condition)}
                                                </div>
                                            )}
                                        </Space>
                                    </Card>
                                ))}
                            </Space>
                        )}
                    </Card>

                    <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={handleCancelForm}>
                                Cancelar
                            </Button>
                            <Button type="primary" htmlType="submit" size="large">
                                {editingFilter ? 'Atualizar Filtro' : 'Criar Filtro'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            )}
        </Modal>
    );
}